/**
 * The Build tab — palette, step list, inspector.
 *
 * Visual Reference: `design/screens/editor-build.html` — the **Blocks palette**
 * (200px, left) and the **Inspector** (320px, right) are built as drawn.
 *
 * THE MIDDLE COLUMN IS NOT. DECISION-014: "M1 Build = reorderable step list +
 * block library + inspector. The mockup's graph becomes an M6 story." The
 * export draws a flow canvas there — four lanes, nine nodes, wire thickness as
 * lead volume — and PRD §3.4 scopes M1 to a "linear step sequence with reorder"
 * with the React Flow canvas at M6, adding "hold the §10.1 line until a named
 * customer hits the list-editor ceiling". So the canvas is replaced by the list
 * the PRD asks for. It had no Visual Reference of its own (Q-045) until
 * DECISION-068 had it drawn, and the 2026-09-17 round-trip drew the collapse
 * and expansion DECISION-076 decided, which Story 20.10 builds.
 * The two panels either side of it are pixel-for-pixel the export's.
 *
 * Stories 6.3 and 20.10.
 */
import { Fragment, useEffect, useId, useRef, useState } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Block, BlockType, Funnel } from '@leadgible/schema'
import { BLOCK_LABEL, PALETTE, newBlockId, newStepId } from './blocks'
import { Inspector } from './Inspector'
import { criterionFor, forgetBlock } from './rubric'
import { moveBlockInStep, previewOf, summaryOf, wordsOf } from './step-card'
import { edit, useEditor } from './store'
import { useShell } from '../shell/workspace'
import { addBlockTo, insertStep } from './vocabulary'

/**
 * THREE COLUMNS AT 1440, AND TWO BOTTOM SHEETS AT 375.
 *
 * The export draws both. Its mobile capture replaces the side columns with
 * sheets — a 36x4 grab handle, "Blocks · Tap to open" collapsed, and the
 * inspector as "Node settings · Budget" with a close control — because 200px
 * and 320px of fixed column leave 375 with nothing in the middle. Built with
 * `order-*` so the DOM puts the steps FIRST, which is what a screen reader and
 * a narrow screen should both meet first, while `lg` restores the drawn
 * left-to-right order.
 */
export function BuildTab({
  onOpenQualify,
}: {
  /** The inspector's "Open the Qualify tab" (Story 20.7), switching tab in place. */
  onOpenQualify: () => void
}) {
  const funnel = useEditor((s) => s.funnel)
  const [selected, setSelected] = useState<string | null>(null)
  const [paletteOpen, setPaletteOpen] = useState(false)

  if (!funnel) return null

  const block = funnel.blocks.find((b) => b.id === selected) ?? null

  return (
    <div className="flex min-h-0 flex-col lg:flex-row lg:items-stretch">
      <Palette
        selected={selected}
        open={paletteOpen}
        onToggle={() => setPaletteOpen((v) => !v)}
        onAdded={(id) => {
          setSelected(id)
          setPaletteOpen(false)
        }}
      />
      <StepList funnel={funnel} selected={selected} onSelect={setSelected} />
      <InspectorPanel
        funnel={funnel}
        block={block}
        onClose={() => setSelected(null)}
        onOpenQualify={onOpenQualify}
      />
    </div>
  )
}

/* ── the inspector column, and the sheet it becomes ─────────────────────── */

function InspectorPanel({
  funnel,
  block,
  onClose,
  onOpenQualify,
}: {
  funnel: Funnel
  block: Block | null
  onClose: () => void
  onOpenQualify: () => void
}) {
  return (
    <aside
      className={[
        'order-3 flex-none bg-mdx-white',
        // 320px. `--mdx-header-column` holds exactly that value under a name
        // meaning something else — Q-041 asks for `--mdx-inspector-width`.
        'lg:w-mdx-header-column lg:overflow-y-auto lg:border-l lg:border-mdx-line lg:px-mdx-16 lg:py-mdx-20',
        // Below lg it is a sheet: rounded top, lifted shadow, its own gutter.
        'rounded-t-mdx-card border-t border-mdx-line px-mdx-16 py-mdx-14 shadow-mdx-card lg:rounded-none lg:border-t-0 lg:shadow-none',
      ].join(' ')}
    >
      <Grabber />
      <div className="mb-mdx-14 flex items-center justify-between gap-mdx-12">
        <span className="block text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
          {block ? `Block · ${BLOCK_LABEL[block.type]}` : 'Inspector'}
        </span>
        {block ? (
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="size-mdx-avatar-sm flex-none cursor-pointer rounded-mdx-control-sm border border-mdx-line bg-mdx-white text-mdx-13 text-mdx-slate lg:hidden"
          >
            <span aria-hidden="true">✕</span>
          </button>
        ) : null}
      </div>
      <Inspector funnel={funnel} block={block} onOpenQualify={onOpenQualify} />
    </aside>
  )
}

/** The sheet's grab handle. Drawn below `lg` only, where a sheet is what it is. */
function Grabber() {
  return (
    <span
      aria-hidden="true"
      className="mx-auto mb-mdx-12 block h-mdx-4 w-mdx-40 rounded-mdx-pill bg-mdx-line lg:hidden"
    />
  )
}

/* ── the palette ────────────────────────────────────────────────────────── */

function Palette({
  selected,
  open,
  onToggle,
  onAdded,
}: {
  /** The block the inspector edits; its step is the open one. */
  selected: string | null
  open: boolean
  onToggle: () => void
  onAdded: (id: string) => void
}) {
  // The vocabulary's `add_question` (Story 27.2): into the open step — the
  // manifest's "palette block → adds a block to the selected step" — or the last
  // step when none is open, or a first step if there is none, because a block that
  // belongs to no step never renders and never scores. One edit, so one undo.
  // Story 26.6: "a new consent block in the builder starts from the org's consent copy".
  // Settings → Compliance holds it and the boot call carries it; with none set, the
  // operation's own default stands. A block already in a funnel is never rewritten.
  const { org } = useShell()
  const consentCopy = org?.consentCopy?.trim() || null
  const add = (type: BlockType) => {
    const id = newBlockId()
    const firstStepId = newStepId()
    const block = type === 'consent' && consentCopy ? { type, id, statement: consentCopy } : { type, id }
    edit(`Add a ${BLOCK_LABEL[type]} block`, (draft) => addBlockTo(draft, block, firstStepId, selected))
    onAdded(id)
  }

  // 200px, via the token that happens to hold that value under a name meaning
  // something else — see the inspector above and Q-041.
  return (
    <aside
      className={[
        'order-2 flex-none bg-mdx-white lg:order-1',
        'lg:w-mdx-row-identity lg:overflow-y-auto lg:border-r lg:border-mdx-line lg:px-mdx-12 lg:py-mdx-20',
        'rounded-t-mdx-card border-t border-mdx-line px-mdx-16 py-mdx-14 shadow-mdx-card lg:rounded-none lg:border-t-0 lg:shadow-none',
      ].join(' ')}
    >
      <Grabber />
      {/* Below `lg` the heading is the disclosure the export draws; at `lg` it
          is the plain eyebrow, and the groups are always open. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="mb-mdx-14 flex w-full cursor-pointer items-center justify-between gap-mdx-12 border-0 bg-transparent p-0 text-left text-mdx-15 font-mdx-semibold text-mdx-ink lg:hidden"
      >
        Blocks
        <span className="text-mdx-13 font-mdx-medium text-mdx-slate">
          {open ? 'Tap to close ▾' : 'Tap to open ▴'}
        </span>
      </button>
      <span className="mb-mdx-14 hidden text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate lg:block">
        Blocks
      </span>
      <div className={[open ? 'flex' : 'hidden lg:flex', 'flex-col gap-mdx-16'].join(' ')}>
        {PALETTE.map((group) => (
          <div key={group.label} className="flex flex-col gap-mdx-6">
            <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
              {group.label}
            </span>
            <div className="flex flex-col gap-mdx-2">
              {group.types.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => add(type)}
                  className="cursor-pointer rounded-mdx-control-sm border border-mdx-line bg-mdx-white px-mdx-10 py-mdx-8 text-left text-mdx-14 font-mdx-medium text-mdx-ink transition-colors ease-mdx-swift hover:bg-mdx-ground"
                >
                  {BLOCK_LABEL[type]}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </aside>
  )
}

/* ── the step list — DECISION-014's replacement for the canvas ──────────── */

function StepList({
  funnel,
  selected,
  onSelect,
}: {
  funnel: Funnel
  selected: string | null
  onSelect: (id: string) => void
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // A drag has to travel before it counts, or every click on a card in a
      // sortable list is read as a one-pixel drag and the card never selects.
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  /**
   * The row to focus once a collapsed card opens. Opening a card replaces the
   * button that was pressed with the card's rows, so without this a keyboard
   * user's focus would fall back to the page; it lands on the row the press
   * selected instead.
   */
  const [focusTarget, setFocusTarget] = useState<string | null>(null)

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = funnel.steps.findIndex((s) => s.id === active.id)
    const to = funnel.steps.findIndex((s) => s.id === over.id)
    if (from < 0 || to < 0) return
    edit('Reorder the steps', (draft) => {
      draft.steps = arrayMove(draft.steps, from, to)
    })
  }

  /**
   * Insert AT a position, not only at the end.
   *
   * The export draws four `+ Add step` dividers for five cards — between them
   * and nowhere else — so the affordance it draws is "put one here", which a
   * push-only add cannot express. The trailing button below the list stays for
   * the case the export has no affordance for at all: appending a sixth.
   */
  // The vocabulary's `add_step` (Story 27.2), which names a step by where it
  // LANDS, not by how many exist: an insert at position 1 that called itself
  // "Step 3" would be wrong the moment it appeared.
  const addStepAt = (position: number) => {
    const id = newStepId()
    edit('Add a step', (draft) => insertStep(draft, id, position))
  }

  const blockCount = funnel.steps.reduce((sum, step) => sum + step.blocks.length, 0)

  return (
    <div className="order-1 flex min-w-0 flex-1 flex-col gap-mdx-14 px-mdx-24 py-mdx-20 lg:order-2">
      {/* The export's own heading for this column, and the count beside it —
          DECISION-068. It is the one place the whole funnel's size is stated. */}
      <div className="flex flex-wrap items-baseline gap-x-mdx-14 gap-y-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">Steps</span>
        <span className="text-mdx-13 text-mdx-slate tabular-nums">
          {funnel.steps.length} {funnel.steps.length === 1 ? 'step' : 'steps'} · {blockCount}{' '}
          {blockCount === 1 ? 'block' : 'blocks'}
        </span>
      </div>

      {funnel.steps.length === 0 ? (
        <p className="m-0 max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
          No steps yet. Add a block from the left and the first step appears with it.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext
            items={funnel.steps.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <ol className="m-0 flex list-none flex-col gap-mdx-10 p-0">
              {funnel.steps.map((step, index) => {
                const blocks = step.blocks
                  .map((id) => funnel.blocks.find((b) => b.id === id))
                  .filter((b): b is Block => b !== undefined)
                return (
                  <Fragment key={step.id}>
                    {index > 0 ? <AddStepDivider onAdd={() => addStepAt(index)} /> : null}
                    <StepCard
                      funnel={funnel}
                      id={step.id}
                      index={index}
                      title={step.title ?? `Step ${index + 1}`}
                      blocks={blocks}
                      scored={blocks.some((b) => criterionFor(funnel, b.id) !== undefined)}
                      current={blocks.some((b) => b.id === selected)}
                      selected={selected}
                      onSelect={onSelect}
                      onOpen={(blockId) => {
                        onSelect(blockId)
                        setFocusTarget(blockId)
                      }}
                      focusTarget={focusTarget}
                      onFocused={() => setFocusTarget(null)}
                    />
                  </Fragment>
                )
              })}
            </ol>
          </SortableContext>
        </DndContext>
      )}

      {/* The end of the list. The export draws dividers BETWEEN cards only, so
          it has no affordance for appending — this is a retained deviation, and
          removing it would leave a funnel unable to grow. Q-051. */}
      <button
        type="button"
        onClick={() => addStepAt(funnel.steps.length)}
        className="cursor-pointer self-start rounded-mdx-control-sm border-0 bg-mdx-brand-tint px-mdx-16 py-mdx-8 text-mdx-13 font-mdx-semibold text-mdx-brand-deep"
      >
        Add a step
      </button>
    </div>
  )
}

/**
 * The `+ Add step` rule between two cards — DECISION-068's drawing.
 *
 * At rest it sits back at `--mdx-opacity-recessed` and comes forward on hover,
 * which is what the export draws (`transition: opacity 150ms`). This site is
 * why that token is not called `disabled`: it is the one use of the value that
 * means "at rest and reachable" rather than "cannot be used", and a name that
 * said `disabled` would have been wrong here in every reading (DECISION-075).
 */
function AddStepDivider({ onAdd }: { onAdd: () => void }) {
  return (
    <li className="flex items-center gap-mdx-10 opacity-mdx-recessed transition-opacity ease-mdx-swift hover:opacity-100 focus-within:opacity-100">
      <span aria-hidden="true" className="h-px flex-1 bg-mdx-line" />
      <button
        type="button"
        onClick={onAdd}
        className="cursor-pointer whitespace-nowrap rounded-mdx-control-sm border border-mdx-line bg-mdx-white px-mdx-12 py-mdx-6 text-mdx-13 font-mdx-semibold text-mdx-brand-deep"
      >
        + Add step
      </button>
      <span aria-hidden="true" className="h-px flex-1 bg-mdx-line" />
    </li>
  )
}

/**
 * One step — DECISION-076, drawn by the 2026-09-17 round-trip, built by 20.10.
 *
 * COLLAPSED until selected: the first block's words as a preview, and one
 * summary line of every block, by the rule `step-card.ts` holds. The SELECTED
 * card, the one holding the block the inspector edits, expands to one row per
 * block. Opening a collapsed card selects its first block, because the
 * inspector edits a BLOCK (6.3's criterion) and a card opened with nothing
 * selected would leave it nothing to edit.
 *
 * The collapsed card's control is the preview panel, NAMED by the preview and
 * DESCRIBED by the summary: pressing it selects that first block, so its name
 * says what it selects.
 *
 * The drawn layout: the handle is a column of its own, and the header and the
 * body stack beside it at 10px.
 */
function StepCard({
  funnel,
  id,
  index,
  title,
  blocks,
  scored,
  current,
  selected,
  onSelect,
  onOpen,
  focusTarget,
  onFocused,
}: {
  funnel: Funnel
  id: string
  index: number
  title: string
  blocks: Block[]
  /** A rubric criterion is sourced from one of this step's blocks. */
  scored: boolean
  /** This step holds the block the inspector is editing. */
  current: boolean
  selected: string | null
  onSelect: (id: string) => void
  /** Open a collapsed card: select its first block and focus that block's row. */
  onOpen: (blockId: string) => void
  focusTarget: string | null
  onFocused: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const lifted = current || isDragging
  const previewId = useId()
  const summaryId = useId()
  const [first] = blocks

  return (
    <li
      ref={setNodeRef}
      aria-current={current ? 'true' : undefined}
      // Transform and transition are what dnd-kit computes per frame; a
      // position is data, not a token, exactly as the band bar's width is.
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={[
        'flex items-start gap-mdx-14 rounded-mdx-card bg-mdx-white px-mdx-18 py-mdx-16 shadow-mdx-card transition-colors ease-mdx-swift',
        // The export compensates the second border pixel with 15px/17px padding
        // on the current card. Neither is on the spacing scale, so the card
        // keeps one padding in both states, and the selected card is 2px taller
        // than drawn: the second pixel, top and bottom. Measured at Story
        // 20.10, where this comment used to say the card does not grow.
        lifted ? 'border-2 border-mdx-brand' : 'border border-mdx-line hover:border-mdx-line-strong',
      ].join(' ')}
    >
      <button
        type="button"
        // The handle carries the drag, not the card: a card that is entirely
        // a drag handle cannot hold buttons, and every block row inside is one.
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${title}`}
        className="flex size-mdx-evidence-icon flex-none cursor-grab items-center justify-center rounded-mdx-control-sm border border-mdx-line bg-mdx-ground text-mdx-13 leading-none text-mdx-slate transition-colors ease-mdx-swift hover:bg-mdx-line-soft"
      >
        <span aria-hidden="true">⠿</span>
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-mdx-10">
        <div className="flex flex-wrap items-baseline gap-x-mdx-12 gap-y-mdx-6">
          <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate tabular-nums">
            Step {index + 1}
          </span>
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink">{title}</span>
          {scored ? (
            // What the export puts on a step whose answers are worth points. It
            // is derived from `rubric.criteria[]` — §3.2's one owner for scoring —
            // and never from anything stored on the step.
            <span className="flex-none rounded-mdx-pill bg-mdx-brand-tint px-mdx-9 py-mdx-3 text-mdx-12 font-mdx-semibold text-mdx-brand-deep">
              Scored
            </span>
          ) : null}
          <span className="ml-auto flex-none whitespace-nowrap text-mdx-13 text-mdx-slate tabular-nums">
            {blocks.length} {blocks.length === 1 ? 'block' : 'blocks'}
          </span>
        </div>

        {first === undefined ? (
          <span className="text-mdx-13 text-mdx-slate">Nothing in this step yet.</span>
        ) : current ? (
          <BlockRows
            funnel={funnel}
            stepId={id}
            blocks={blocks}
            selected={selected}
            onSelect={onSelect}
            focusTarget={focusTarget}
            onFocused={onFocused}
          />
        ) : (
          <button
            type="button"
            onClick={() => onOpen(first.id)}
            aria-labelledby={previewId}
            aria-describedby={summaryId}
            className="block w-full cursor-pointer rounded-mdx-control border-0 bg-mdx-ground px-mdx-12 py-mdx-10 text-left text-mdx-15 text-mdx-ink"
          >
            <span id={previewId}>{previewOf(funnel, first)}</span>
            <span id={summaryId} className="mt-mdx-2 block text-mdx-13 text-mdx-slate">
              {summaryOf(funnel, blocks)}
            </span>
          </button>
        )}
      </div>
    </li>
  )
}

/**
 * The selected card's rows, and the only place a block moves — DECISION-153.
 *
 * ITS OWN DndContext. The step list's context sorts steps; a second one here
 * means a row's handle can only reorder this step's blocks, and a block can
 * never be dropped into another step, which nothing draws.
 */
function BlockRows({
  funnel,
  stepId,
  blocks,
  selected,
  onSelect,
  focusTarget,
  onFocused,
}: {
  funnel: Funnel
  stepId: string
  blocks: Block[]
  selected: string | null
  onSelect: (id: string) => void
  focusTarget: string | null
  onFocused: () => void
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // The same travel the step handle needs, for the same reason.
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      // An arrow whose target is above the middle of the screen SCROLLS the
      // page rather than moving the row, and the row it is over changes only as
      // the scroll runs. Smoothly, Space in that window drops the row back where
      // it began: measured, a drop 60 ms after the arrow did, and at 375px so
      // did one 260 ms after. Instant, the target settles in two frames.
      scrollBehavior: 'auto',
    }),
  )

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    edit('Reorder the blocks', (draft) => {
      moveBlockInStep(draft, stepId, String(active.id), String(over.id))
    })
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
        <ol className="m-0 flex list-none flex-col gap-mdx-8 p-0">
          {blocks.map((block) => (
            <BlockRow
              key={block.id}
              funnel={funnel}
              block={block}
              active={block.id === selected}
              onSelect={onSelect}
              focus={focusTarget === block.id}
              onFocused={onFocused}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  )
}

/**
 * One row of the selected card, as drawn: `⠿`, the type as a small label, and
 * the block's words.
 *
 * THREE CONTROLS WHERE THE DRAWING HAS ONE. The export's row is a single button
 * with the handle inside it, and that cannot carry a working handle or a
 * Delete, because a button inside a button is not a button. So the row is the
 * drawn box and the handle, the selection and the Delete are its controls. The
 * selection is named by the block's words and described by its type.
 *
 * `Delete` stays, on the selected row only: a listed deviation (DECISION-153).
 * The drawing has no block-level Delete anywhere — its `Duplicate` and
 * `Delete` belong to an option row — and without this one a block could not be
 * removed at all.
 *
 * The glyph's `line-height: 1.5` has no token either; it is built at
 * `--mdx-leading-reason`, 1.55, a near-miss rather than a new value.
 */
function BlockRow({
  funnel,
  block,
  active,
  onSelect,
  focus,
  onFocused,
}: {
  funnel: Funnel
  block: Block
  active: boolean
  onSelect: (id: string) => void
  /** This row is the one a just-opened card selected. */
  focus: boolean
  onFocused: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id })
  const words = wordsOf(funnel, block)
  const label = previewOf(funnel, block)
  const typeId = useId()
  const wordsId = useId()
  const selectRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!focus) return
    selectRef.current?.focus()
    onFocused()
  }, [focus, onFocused])

  return (
    <li
      ref={setNodeRef}
      // Translate, not Transform: rows differ in height, and a scale computed
      // between two of them would stretch the text of the one in motion.
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={[
        // The drawn row is a button at the browser's own `line-height: normal`,
        // about 1.14 here, which no token names; it is built at
        // `--mdx-leading-heading`, 1.16, as `usage-cost` D6 and the review
        // queue's rows did. Set on the row so the Delete inherits it and a
        // selected row is no taller than the others.
        'flex items-start gap-mdx-10 rounded-mdx-control border px-mdx-11 py-mdx-9 leading-mdx-heading',
        active
          ? 'border-mdx-brand bg-mdx-brand-tint'
          : 'border-transparent bg-mdx-ground transition-colors ease-mdx-swift hover:bg-mdx-line-soft',
        // Above its neighbours while it travels, or a row dragged down would
        // pass under the one after it.
        isDragging ? 'relative z-10' : '',
      ].join(' ')}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${label}`}
        className="w-mdx-14 flex-none cursor-grab border-0 bg-transparent p-0 text-center text-mdx-14 leading-mdx-reason text-mdx-slate"
      >
        <span aria-hidden="true">⠿</span>
      </button>
      <button
        ref={selectRef}
        type="button"
        onClick={() => onSelect(block.id)}
        aria-current={active ? 'true' : undefined}
        aria-labelledby={words === null ? typeId : wordsId}
        aria-describedby={words === null ? undefined : typeId}
        className="flex min-w-0 flex-1 cursor-pointer flex-col border-0 bg-transparent p-0 text-left"
      >
        <span id={typeId} className={['text-mdx-13', active ? 'text-mdx-brand-deep' : 'text-mdx-slate'].join(' ')}>
          {BLOCK_LABEL[block.type]}
        </span>
        {words === null ? null : (
          <span id={wordsId} className="text-mdx-15 text-mdx-ink">
            {words}
          </span>
        )}
      </button>
      {active ? (
        <button
          type="button"
          aria-label={`Delete ${label}`}
          onClick={() => edit(`Delete ${label}`, (draft) => forgetBlock(draft, block.id))}
          className="flex-none cursor-pointer rounded-mdx-control-sm border border-mdx-line bg-mdx-white px-mdx-10 py-mdx-6 text-mdx-13 font-mdx-semibold text-mdx-error"
        >
          Delete
        </button>
      ) : null}
    </li>
  )
}
